import { useCallback, useEffect, useRef, useState } from "react";
import { bankService, userService } from "../../_services";

export const isVerified = value => value === true || value === 1 || value === "1";

export function propertyReadiness(snapshot, host, identityRequired) {
  const detail = snapshot.host || host || {};
  const loading = snapshot.phase === "loading" || snapshot.phase === "idle";
  const emailVerified = isVerified(detail.personalEmailVerification);
  const phoneVerified = isVerified(detail.phoneVerification);
  const identityVerified = isVerified(detail.idVerificationStatus);
  const payoutVerified = isVerified(detail.payoutVerification);
  const paymentReady = snapshot.phase === "ready" && payoutVerified && snapshot.accounts.length > 0;
  const blockers = [];
  if (loading) blockers.push("Checking your verification and payout information…");
  if (snapshot.error) blockers.push(snapshot.error);
  if (!emailVerified) blockers.push("Verify your email address.");
  if (!phoneVerified) blockers.push("Verify your phone number.");
  if (identityRequired && !identityVerified) blockers.push("Complete Selfie & ID verification and wait for confirmation.");
  if (snapshot.phase === "ready") {
    if (!payoutVerified) blockers.push("Payout confirmation is pending. Complete payout setup, then retry after confirmation.");
    if (!snapshot.accounts.length) blockers.push("No payout bank account was found. Check your payout setup.");
  }
  return {
    emailVerified, phoneVerified, identityVerified, paymentReady, loading, blockers,
    ready: snapshot.phase === "ready" && emailVerified && phoneVerified &&
      (!identityRequired || identityVerified) && paymentReady,
  };
}

export function usePropertyVerification({ active, hostDetail, setDetail, identityRequired }) {
  const [snapshot, setSnapshot] = useState({ phase: "idle", host: null, accounts: [], error: "" });
  const snapshotRef = useRef(snapshot);
  const revision = useRef(0);
  const publishSnapshot = useCallback(value => {
    snapshotRef.current = value;
    setSnapshot(value);
  }, []);

  const refresh = useCallback(async () => {
    const current = ++revision.current;
    publishSnapshot({ ...snapshotRef.current, phase: "loading", error: "" });
    const [profile, banks] = await Promise.allSettled([
      Promise.resolve().then(() => userService.getDetail({})),
      Promise.resolve().then(() => bankService.bankList()),
    ]);
    if (current !== revision.current) return;
    const profileOK = profile.status === "fulfilled" && profile.value?.status === true && profile.value.data?.id != null;
    const banksOK = banks.status === "fulfilled" && banks.value?.status === true && Array.isArray(banks.value.data?.data);
    const host = profileOK ? profile.value.data : null;
    if (host) setDetail(host);
    publishSnapshot({
      phase: profileOK && banksOK ? "ready" : "error",
      host, accounts: banksOK ? banks.value.data.data : [],
      error: !profileOK ? "Unable to check your verification status. Please retry." :
        !banksOK ? "Unable to check your payout bank account. Please retry." : "",
    });
    return host;
  }, [publishSnapshot, setDetail]);

  useEffect(() => {
    if (active) refresh();
    return () => { revision.current += 1; };
  }, [active, refresh]);

  return {
    ...propertyReadiness(snapshot, hostDetail, identityRequired), refresh,
    canSubmit: () => propertyReadiness(snapshotRef.current, hostDetail, identityRequired).ready,
  };
}
